/** Parse a YYYY-MM-DD string to a local Date at noon (avoids timezone day-rollover). */
export const parseYmd = (value: string): Date => {
  const [y, m, d] = value.split('-').map(Number);
  return new Date(y, (m || 1) - 1, d || 1, 12, 0, 0);
};

export const toYmd = (date: Date): string =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
    2,
    '0',
  )}-${String(date.getDate()).padStart(2, '0')}`;

export const addDays = (date: Date, days: number): Date => {
  const next = new Date(date);
  next.setDate(date.getDate() + days);
  return next;
};

export const todayYmd = (): string => toYmd(new Date());

/** What a date-rail / picker selection should do to the loaded-days window. */
export type DatePickPlan =
  /** Restart the window at the picked date — that day renders at the top and the following
   *  days stream in below it. */
  | { readonly kind: 'reset' }
  /** Pick is already the first rendered day — keep the window, just re-anchor to it. */
  | { readonly kind: 'anchor' };

/** Decide how a selected date maps onto the currently loaded window. Every pick RESTARTS the
 *  window at the picked day: a pick means "show me this day and what follows", so the picked
 *  day belongs at the top with the next days below. The previous design kept/loaded the days
 *  BETWEEN the old window and the pick above it, which produced the WAY-15922 family of bugs —
 *  clicks stranded at capacity, landings drifting onto the previous day as the sections above
 *  reflowed, and "click the 24th, see the 23rd above it" reading backwards. Re-picking the day
 *  already at the top keeps the window (no reload flash) and only re-anchors. */
export const planDatePick = (
  date: string,
  loadedDates: ReadonlyArray<string>,
): DatePickPlan =>
  loadedDates.length > 0 && loadedDates[0] === date
    ? { kind: 'anchor' }
    : { kind: 'reset' };

const capitalize = (value: string): string =>
  value.charAt(0).toUpperCase() + value.slice(1);

/** Return the locale if `Intl.DateTimeFormat` actually supports it; otherwise fall back to
 *  en-US. Defense-in-depth: callers should already be sanitising, but `Intl.DateTimeFormat`
 *  throws a RangeError on structurally invalid tags (i18next's 'path' detector can pick up
 *  URL segments like "experiences" or "page"), which unwinds React render and trips the
 *  top-level ErrorBoundary. `supportedLocalesOf` also filters out valid-shaped but
 *  unsupported tags (returns `[]`), so we fall back to en-US in that case too. */
export const safeLocale = (locale: string): string => {
  try {
    const supported = Intl.DateTimeFormat.supportedLocalesOf(locale);
    return supported.length > 0 ? supported[0] : 'en-US';
  } catch {
    return 'en-US';
  }
};

const ordinal = (n: number): string => {
  const suffixes = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return `${n}${suffixes[(v - 20) % 10] || suffixes[v] || suffixes[0]}`;
};

/** "07:00:00" / ISO datetime -> locale time, e.g. en: "7:00am", fr: "07:00". */
export const formatTime = (iso: string, locale: string): string => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return new Intl.DateTimeFormat(safeLocale(locale), {
    hour: 'numeric',
    minute: '2-digit',
  })
    .format(date)
    .replace(/\s/g, '')
    .replace('AM', 'am')
    .replace('PM', 'pm');
};

interface DurationLabels {
  readonly hour: string;
  readonly hours: string;
  readonly minutes: string;
}

/** "45 min" / "1 hour" / "1 hour 30 min" from a minutes count. Callers pass i18n-resolved labels
 *  so the unit words render in the active locale; English defaults are used if omitted. */
export const formatDuration = (
  minutes?: number | null,
  labels: DurationLabels = { hour: 'hour', hours: 'hours', minutes: 'min' },
): string => {
  if (!minutes || minutes <= 0) return '';
  if (minutes < 60) return `${minutes} ${labels.minutes}`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  const hoursLabel = hours > 1 ? labels.hours : labels.hour;
  return rest
    ? `${hours} ${hoursLabel} ${rest} ${labels.minutes}`
    : `${hours} ${hoursLabel}`;
};

/** Format the set of durations offered at a single schedule-row slot, e.g. "30 min, 1 hour".
 *  Durations are de-duplicated and sorted ascending, joined with a comma (a dash would read as a
 *  range and falsely imply the in-between durations are bookable), and capped at `maxShown` with a
 *  "+N more" tail. Callers pass i18n-resolved unit labels and the "+N more" formatter so both
 *  render in the active locale. */
export const formatDurations = (
  minutesList: ReadonlyArray<number | null | undefined>,
  labels?: DurationLabels,
  options?: {
    readonly maxShown?: number;
    readonly more?: (count: number) => string;
  },
): string => {
  const maxShown = options?.maxShown ?? 3;
  const unique = Array.from(
    new Set(
      minutesList.filter(
        (minutes): minutes is number => !!minutes && minutes > 0,
      ),
    ),
  ).sort((a, b) => a - b);
  if (unique.length === 0) return '';
  const shown = unique
    .slice(0, maxShown)
    .map((minutes) => formatDuration(minutes, labels))
    .join(', ');
  const remaining = unique.length - maxShown;
  return remaining > 0 && options?.more
    ? `${shown} ${options.more(remaining)}`
    : shown;
};

interface HeadingLabels {
  readonly today?: string;
  readonly tomorrow?: string;
}

/**
 * "Friday, August 20th" (ordinal only applied for English locales). When `labels.today` /
 * `labels.tomorrow` are provided, the weekday is swapped for those translated terms on the
 * matching dates (e.g. "Today, June 25th").
 */
export const formatHeading = (
  ymd: string,
  locale: string,
  labels?: HeadingLabels,
): string => {
  const date = parseYmd(ymd);
  const safe = safeLocale(locale);
  const month = capitalize(
    new Intl.DateTimeFormat(safe, { month: 'long' }).format(date),
  );
  const day = safe.startsWith('en')
    ? ordinal(date.getDate())
    : `${date.getDate()}`;

  const today = todayYmd();
  const tomorrow = toYmd(addDays(parseYmd(today), 1));
  const lead = resolveLead(ymd, today, tomorrow, locale, date, labels);

  return `${lead}, ${month} ${day}`;
};

const resolveLead = (
  ymd: string,
  today: string,
  tomorrow: string,
  locale: string,
  date: Date,
  labels?: HeadingLabels,
): string => {
  if (ymd === today && labels?.today) return labels.today;
  if (ymd === tomorrow && labels?.tomorrow) return labels.tomorrow;
  return capitalize(
    new Intl.DateTimeFormat(safeLocale(locale), { weekday: 'long' }).format(
      date,
    ),
  );
};
